const MessageBlock = defineAsyncComponent(() => import("@/components/message-block.vue"));
const VueDropzone = defineAsyncComponent(() => import("vue2-dropzone-vue3"));
import { arrayMoveImmutable } from "array-move";
import { Big } from "big.js";
import { calculateArtworkGeometry, calculateAndFormatArtworkGeometry, parseAndFormatArtworkGeometry } from "@/lib/geometry.js";
import { computed, reactive, toRaw } from "vue";
import { DateTime } from "luxon";
import { defineAsyncComponent } from "vue";
const VueSpinner = defineAsyncComponent(() => import("vue3-spinners").then((m) => m.VueSpinner));
import ImagePending from "@/components/image-pending.vue";
import clone from "clone";
import collect from "collect.js";
import debounce from "debounce";
import FormatterMixin from "@/mixins/formatter.js";
import qs from "querystringify";

import {
  IMAGE_URL,
  CLOTHING_MOCKUP_URL,
  PHONECASE_MOCKUP_URL,
  HAT_MOCKUP_URL,
  BEANIE_MOCKUP_URL,
  TOTE_MOCKUP_URL,
  DRINKWARE_MOCKUP_URL,
  DRINKWARE_TEXTURE_URL,
  STICKER_MOCKUP_URL,
  placementToBlankLocationMap,
  placementToBoxMap,
  productTypes,
  salesMethods,
  productionMethods,
  sortFunctionFactory,
  getCustomSize,
  getOffsetRange,
  getPlacements,
  getBackPlacements,
  backNeckPlacement,
  customPlacements,
  allowsSecondLocation,
  allowsBackPrimary,
  primarySideOptions,
  screenPrintInkTypes,
  screenPrintInkEffects,
} from "@/lib/product-creator.js";

import {
  getItemGroupKey as getItemGroupKeyUtil,
  itemsGroupedByCase as itemsGroupedByCaseUtil,
  itemsGroupedByType as itemsGroupedByTypeUtil,
  itemsGroupedByDrinkwareType as itemsGroupedByDrinkwareTypeUtil,
} from "@/lib/item-grouping.js";

// toISO() options for a <input type="datetime-local"> value (local, to the minute).
const DATETIME_LOCAL_INPUT_FORMAT = {
  format: "extended",
  suppressSeconds: true,
  suppressMilliseconds: true,
  includeOffset: false,
  extendedZone: false,
};

export default {
  mixins: [FormatterMixin],

  components: {
    MessageBlock,
    VueDropzone,
    ImagePending,
    VueSpinner,
  },

  props: {
    // Every price table, surcharge, margin and default the pricing below uses
    // (ProductPricing::wizardPricingTables), including the signed-in seller's
    // own base adjustment. Passed by both pages' controllers.
    pricingTables: { type: Object, required: true },
  },

  data() {
    return {
      // The signed-in seller's own base adjustment: the page's at load, and the
      // sign-in response's after a mid-flow sign-in (create-a-product's
      // applySignedInUser), so it never waits on a request.
      userBaseAdjustment: this.pricingTables.userBaseAdjustment,
    };
  },

  computed: {
    // The screen-print ink options with their base-price adders.
    inkTypes() {
      return screenPrintInkTypes(this.pricingTables);
    },

    inkEffects() {
      return screenPrintInkEffects(this.pricingTables);
    },

    filteredBundleItems() {
      if (!this.activeColor) return [];
      const items = this.input.productType === this.productTypes.APPAREL ? this.itemsGroupedByType(this.activeColor.bundle.items, true) : this.activeColor.bundle.items;
      return items.filter((item) => item.isIncludedAndValid && (!("activeGroupKey" in this.activeColor) || this.activeColor.activeGroupKey === this.getItemGroupKey(item)));
    },

    customSize() {
      return getCustomSize(this.input.productType, this.input.placement);
    },

    // The primary location's Offset X / Y range: ±1in, a hat's or beanie's
    // vertical nudge within its embroidery area (PRO-1011), or on apparel no
    // higher than the fulfiller can print.
    offsetRange() {
      return getOffsetRange(this.input.productType, this.input.printGeometry, this.rasterGeometry, this.input.placement, this.input.productionMethod);
    },

    // The back print's range, on the same terms.
    secondOffsetRange() {
      const second = this.input.secondLocation;

      return second ? getOffsetRange(this.input.productType, second.printGeometry, this.secondRasterGeometry, second.placement, second.productionMethod) : null;
    },

    placements() {
      if (this.primarySide === "back") {
        return getBackPlacements(this.input.productType, this.input.productionMethod);
      }

      return getPlacements(this.input.productType, this.input.productionMethod);
    },

    // Which side of the garment the primary (first) location prints on. A
    // back-only product (PRO-3173) puts its one location on the back; the
    // Front/Back choice is offered wherever canChooseBackPrimary.
    primarySide() {
      return placementToBlankLocationMap[this.input.placement] === "back" ? "back" : "front";
    },

    // Hidden once a back print is added: the product then prints on both
    // sides, so its primary location stays on the front.
    canChooseBackPrimary() {
      return allowsBackPrimary(this.input.productType, this.input.productionMethod) && !this.input.secondLocation;
    },

    // "Add a Back Print": on the server's gate (Product::supportsBackPrint),
    // and only to a front/pocket primary.
    canAddSecondLocation() {
      return allowsSecondLocation(this.input.productType, this.input.salesMethod, this.input.productionMethod) && this.primarySide !== "back" && !this.input.secondLocation;
    },

    // A screen-printed back print has its own inks and source file.
    secondIsScreenPrint() {
      return this.input.secondLocation?.productionMethod === this.productionMethods.SCREENPRINT;
    },

    // Second print location (back print) — shared by the wizard and the
    // account edit page, both of which model it as input.secondLocation.
    secondPlacements() {
      if (!this.input.secondLocation) {
        return [];
      }

      return getBackPlacements(this.input.productType, this.input.secondLocation.productionMethod);
    },

    secondCustomSize() {
      return getCustomSize(this.input.productType, this.input.secondLocation?.placement);
    },

    // The back-neck placement pins the Y offset to zero (Monster's BN origin
    // is fixed 2in below the collar) — both pages disable the input off this.
    secondIsBackNeck() {
      return this.input.secondLocation?.placement === backNeckPlacement;
    },

    primaryIsBackNeck() {
      return this.input.placement === backNeckPlacement;
    },

    c_isProBono: {
      get() {
        let isProBono = this.isProBono;

        if (this.user?.paymentType) {
          isProBono = this.user.paymentType === "probono";
        }

        return isProBono;
      },
      set(newValue) {
        if (!this.user || this.user.paymentType !== "probono") {
          this.isProBono = newValue;
          this.resetItemPricingToDefault();
        }
      },
    },

    baseProfit() {
      let baseProfit = this.pricingTables.defaultProfit;

      if (this.input.productType === this.productTypes.STICKER) {
        baseProfit = this.pricingTables.stickerDefaultProfit;
      }

      if (this.c_isProBono) {
        baseProfit = 0;
      }

      return baseProfit;
    },

    colorLimit() {
      if (this.user) {
        return this.user.tier.colorBundleLimit;
      }

      return 10;
    },

    showRaster() {
      return this.input.artwork.rasterArtworkPathInfo !== null;
    },

    showSecondRaster() {
      return !!this.input.secondLocation?.artwork.rasterArtworkPathInfo;
    },

    activeColor() {
      return this.input.colors.find((color) => color.isActive);
    },

    activeItem() {
      if (this.activeColor) {
        for (const item of this.activeColor.bundle.items) {
          if (item.isActive) {
            return item;
          }
        }
      }

      return undefined;
    },

    itemsGroupedByTypePrice() {
      if (this.input.productType !== this.productTypes.APPAREL) {
        return [];
      }

      const items = this.input.colors.flatMap((color) => color.bundle.items);

      const types = [];

      // Long sleeves group with the tees.
      const typeMap = (item) => (["3501", "3501CVC", "3511", "3513"].includes(item.blank.code) ? "Tee" : item.blank.type);

      items.forEach((item) => {
        const name = typeMap(item);
        let matchingType = types.find((type) => type.name === name);

        if (!matchingType) {
          matchingType = {
            name: name,
            priceGroups: [],
            show: false,
          };

          types.push(matchingType);
        }

        // The blank's catalog price group (catalog_blanks.price_group): a heading and grouping key only.
        const priceLabel = item.blank.price_group ?? "Other";
        const priceKey = priceLabel;

        let priceGroup = matchingType.priceGroups.find((priceGroup) => priceGroup.key === priceKey);

        if (!priceGroup) {
          priceGroup = {
            key: priceKey,
            label: priceLabel,
            blankIds: [],
            items: [],
            show: false,
          };

          matchingType.priceGroups.push(priceGroup);
        }

        priceGroup.items.push(item);

        if (!priceGroup.blankIds.some((id) => id === item.blank.id)) {
          priceGroup.blankIds.push(item.blank.id);
        }
      });

      types.forEach((type) => {
        type.priceGroups.forEach((priceGroup) => {
          priceGroup.show = priceGroup.items.some((item) => item.isIncludedAndValid);
          priceGroup.displayItem = priceGroup.items.find((item) => item.isIncludedAndValid) ?? priceGroup.items[0];
        });

        type.show = type.priceGroups.some((priceGroup) => priceGroup.show);
      });

      return types;
    },

    itemsGroupedByStyleFinish() {
      if (this.input.productType !== this.productTypes.PHONECASE) {
        return [];
      }

      const items = this.input.colors.flatMap((color) => color.bundle.items);

      const styles = [];

      items.forEach((item) => {
        let style = item.style;

        if (item.magsafe) {
          style = `${style} MagSafe`;
        }

        let matchingStyle = styles.find((s) => s.name === style);

        if (!matchingStyle) {
          matchingStyle = {
            name: style,
            finishGroups: [],
          };

          styles.push(matchingStyle);
        }

        // Group finishes
        const finish = item.finish;

        let finishGroup = matchingStyle.finishGroups.find((finishGroup) => finishGroup.key === finish);

        if (!finishGroup) {
          finishGroup = {
            key: finish,
            label: finish,
            codes: [],
            items: [],
          };

          matchingStyle.finishGroups.push(finishGroup);
        }

        finishGroup.items.push(item);

        if (!finishGroup.codes.some((code) => code === item.code)) {
          finishGroup.codes.push(item.code);
        }
      });

      return styles;
    },

    itemsGroupedByStylePrice() {
      if (this.input.productType !== this.productTypes.HAT && this.input.productType !== this.productTypes.BEANIE) {
        return [];
      }

      const items = this.input.colors.flatMap((color) => color.bundle.items);

      const styleName = this.input.productType === this.productTypes.BEANIE ? "Beanies" : "Hats";

      const style = {
        name: styleName,
        priceGroups: [],
      };

      const styles = [style];

      items.forEach((item) => {
        const matchingStyle = style;

        // Group prices
        let priceLabel = "Other";

        if (this.input.productType === this.productTypes.BEANIE) {
          priceLabel = "Beanies"; // TODO: actual beanie codes
        } else if (["yupoong|6245cm", "yupoong|6606", "yupoong|6089m", "port authority|c932", "yupoong|6789m"].includes(item.code)) {
          priceLabel = "Hats";
        }

        const priceKey = priceLabel;

        let priceGroup = matchingStyle.priceGroups.find((priceGroup) => priceGroup.key === priceKey);

        if (!priceGroup) {
          priceGroup = {
            key: priceKey,
            label: priceLabel,
            items: [],
          };

          matchingStyle.priceGroups.push(priceGroup);
        }

        priceGroup.items.push(item);
      });

      return styles;
    },

    itemsGroupedForTotes() {
      if (this.input.productType !== this.productTypes.TOTE) {
        return [];
      }

      const items = this.input.colors.flatMap((color) => color.bundle.items);

      const style = {
        name: "Totes",
        priceGroups: [],
      };

      const styles = [style];

      items.forEach((item) => {
        const matchingStyle = style;

        // Group prices
        let priceLabel = "Other";

        if (["OAD113"].includes(item.code)) {
          priceLabel = "Totes";
        }

        const priceKey = priceLabel;

        let priceGroup = matchingStyle.priceGroups.find((priceGroup) => priceGroup.key === priceKey);

        if (!priceGroup) {
          priceGroup = {
            key: priceKey,
            label: priceLabel,
            items: [],
          };

          matchingStyle.priceGroups.push(priceGroup);
        }

        priceGroup.items.push(item);
      });

      return styles;
    },

    itemsGroupedForDrinkwareTypes() {
      if (this.input.productType !== this.productTypes.DRINKWARE) {
        return [];
      }

      const items = this.input.colors.flatMap((color) => color.bundle.items);

      const type = {
        name: "Drinkware",
        priceGroups: [],
      };

      const types = [type];

      items.forEach((item) => {
        const matchingType = type;

        // Group prices
        const priceLabel = item.name;
        const priceKey = priceLabel;

        let priceGroup = matchingType.priceGroups.find((priceGroup) => priceGroup.key === priceKey);

        if (!priceGroup) {
          priceGroup = {
            key: priceKey,
            label: priceLabel,
            codes: [],
            items: [],
          };

          matchingType.priceGroups.push(priceGroup);
        }

        priceGroup.items.push(item);

        if (!priceGroup.codes.some((code) => code === item.code)) {
          priceGroup.codes.push(item.code);
        }
      });

      return types;
    },

    itemsGroupedForPricing() {
      let groups = [];

      if (this.input.productType === this.productTypes.APPAREL) {
        groups = this.itemsGroupedByTypePrice.map((type) => ({
          name: type.name,
          showHeading: true,
          show: type.show,
          priceGroups: type.priceGroups,
        }));
      } else if (this.input.productType === this.productTypes.PHONECASE) {
        groups = this.itemsGroupedByStyleFinish.map((style) => ({
          name: style.name,
          showHeading: true,
          show: true,
          priceGroups: style.finishGroups,
        }));
      } else if (this.input.productType === this.productTypes.HAT || this.input.productType === this.productTypes.BEANIE) {
        groups = this.itemsGroupedByStylePrice.map((style) => ({
          name: style.name,
          showHeading: false,
          show: true,
          priceGroups: style.priceGroups,
        }));
      } else if (this.input.productType === this.productTypes.TOTE) {
        groups = this.itemsGroupedForTotes.map((style) => ({
          name: style.name,
          showHeading: false,
          show: true,
          priceGroups: style.priceGroups,
        }));
      } else if (this.input.productType === this.productTypes.DRINKWARE) {
        groups = this.itemsGroupedForDrinkwareTypes.map((type) => ({
          name: type.name,
          showHeading: true,
          show: true,
          priceGroups: type.priceGroups,
        }));
      } else if (this.input.productType === this.productTypes.STICKER) {
        groups = this.itemsGroupedForStickerTypes.map((type) => ({
          name: type.name,
          showHeading: false,
          show: true,
          priceGroups: type.priceGroups,
        }));
      }

      // Ensure every price group has a displayItem pointing to the first valid item (or fallback to items[0])
      groups.forEach((group) => {
        group.priceGroups.forEach((priceGroup) => {
          if (!priceGroup.displayItem) {
            priceGroup.displayItem = priceGroup.items.find((item) => item.isIncludedAndValid) ?? priceGroup.items[0];
          }

          // Sanity check: warn when valid items in an apparel price group have inconsistent retail values.
          // Apparel-only: other product types group by finish/model/size where differing retails are legitimate.
          if (this.input.productType === this.productTypes.APPAREL) {
            const validItems = priceGroup.items.filter((item) => item.isIncludedAndValid);
            if (validItems.length > 1) {
              const expectedRetail = validItems[0].retail;
              const outOfSync = validItems.filter((item) => item.retail !== expectedRetail);
              if (outOfSync.length > 0) {
                console.warn(
                  `[Pricing] Price group "${priceGroup.label}" has out-of-sync retail values:`,
                  validItems.map((item) => ({ blank: item.blank.code, retail: item.retail, production_method: item.production_method, sales_method: item.sales_method })),
                );
              }
            }
          }
        });
      });

      return groups;
    },

    itemsGroupedForStickerTypes() {
      if (this.input.productType !== this.productTypes.STICKER) {
        return [];
      }

      const items = this.input.colors.flatMap((color) => color.bundle.items);

      const type = {
        name: "Stickers",
        priceGroups: [],
      };

      const types = [type];

      items.forEach((item) => {
        const matchingType = type;

        // Group prices
        const priceLabel = "Stickers";

        const priceKey = priceLabel;

        let priceGroup = matchingType.priceGroups.find((priceGroup) => priceGroup.key === priceKey);

        if (!priceGroup) {
          priceGroup = {
            key: priceKey,
            label: priceLabel,
            codes: [],
            items: [],
          };

          matchingType.priceGroups.push(priceGroup);
        }

        priceGroup.items.push(item);

        if (!priceGroup.codes.some((code) => code === item.code)) {
          priceGroup.codes.push(item.code);
        }
      });

      return types;
    },

    mockupWidth() {
      if (this.input.productType === this.productTypes.DRINKWARE) {
        return 512;
      }

      return 1000;
    },

    campaignStartMin() {
      if (this.mockup?.preorderStatus === "live") {
        return null;
      }

      return DateTime.now().set({ second: 0, millisecond: 0 }).toISO(DATETIME_LOCAL_INPUT_FORMAT);
    },

    // A live campaign's window is anchored to its saved start, not the input.
    campaignStartForWindow() {
      return this.mockup?.preorderStatus === "live" ? this.mockup.campaignStart : this.input.campaignStart;
    },

    campaignEndMin() {
      return DateTime.fromISO(this.campaignStartForWindow).plus({ hours: 24 }).toISO(DATETIME_LOCAL_INPUT_FORMAT);
    },

    campaignEndMax() {
      return DateTime.fromISO(this.campaignStartForWindow).plus({ days: 30 }).toISO(DATETIME_LOCAL_INPUT_FORMAT);
    },
  },

  methods: {
    loadColorsItemsAndPricing(activeBlankColors) {
      const activeBlankColorsGroupedByBundleName = collect(activeBlankColors).groupBy((activeBlankColor) => {
        const bundle = this.bundles.find((bundle) => {
          return bundle.items.some((item) => {
            return (
              item.production_method === this.input.productionMethod &&
              item.sales_method === this.input.salesMethod &&
              item.blank.id == activeBlankColor.blankId &&
              item.color.id == activeBlankColor.colorId
            );
          });
        });

        // No matching bundle, return a no-op string
        if (bundle === undefined) {
          return "no match";
        }

        return bundle.name;
      });

      activeBlankColorsGroupedByBundleName.each((activeBlankColors, bundleName) => {
        if (bundleName === "no match") {
          // continue
          return true;
        }

        const bundle = this.bundles.find((bundle) => bundle.name === bundleName);

        const position = this.input.colors.length + 1;

        const color = {
          name: bundle.name,
          position: position,
          isEditing: false,
          isActive: false,
          bundle: bundle,
        };

        color.bundle.items.forEach((item) => {
          const blankColor = activeBlankColors.first((activeBlankColor) => {
            return (
              item.production_method === this.input.productionMethod &&
              item.sales_method === this.input.salesMethod &&
              item.blank.id == activeBlankColor.blankId &&
              item.color.id == activeBlankColor.colorId
            );
          });

          item.isIncluded = item.isValid && !!blankColor;
          item.isDefault = !!(blankColor && blankColor.isDefault);

          if (blankColor) {
            this.initItemPricing(item, blankColor.cut, blankColor.bonusPriceAdjustment);
          }
        });

        this.input.colors.push(color);
      });

      // Keep the hydrated default variant in the primary color (colors[0]).
      const defaultColorIndex = this.input.colors.findIndex((color) => color.bundle.items.some((item) => item.isDefault));

      if (defaultColorIndex > 0) {
        this.input.colors = arrayMoveImmutable(this.input.colors, defaultColorIndex, 0);
      }

      this.syncPriceGroups();
    },

    getItemGroupKey(item) {
      return getItemGroupKeyUtil(this.input.productType, item);
    },

    itemsGroupedByCase(items) {
      return itemsGroupedByCaseUtil(items, this.input.productType);
    },

    itemsGroupedByType(items, flatten = false) {
      return itemsGroupedByTypeUtil(items, flatten);
    },

    itemsGroupedByDrinkwareType(items) {
      return itemsGroupedByDrinkwareTypeUtil(items, this.input.productType);
    },

    // Image rendering
    tryGetRenderStatus(query) {
      if (query in this.renderStatusCache) {
        return this.renderStatusCache[query];
      } else {
        return (this.renderStatusCache[query] = {
          loaded: false,
          error: false,
        });
      }
    },

    imageUrl(query) {
      return `${IMAGE_URL}${query}`;
    },

    mockupUrl(query) {
      let url = null;

      if (this.input.productType === this.productTypes.APPAREL) {
        url = CLOTHING_MOCKUP_URL;
      } else if (this.input.productType === this.productTypes.PHONECASE) {
        url = PHONECASE_MOCKUP_URL;
      } else if (this.input.productType === this.productTypes.HAT) {
        url = HAT_MOCKUP_URL;
      } else if (this.input.productType === this.productTypes.BEANIE) {
        url = BEANIE_MOCKUP_URL;
      } else if (this.input.productType === this.productTypes.TOTE) {
        url = TOTE_MOCKUP_URL;
      } else if (this.input.productType === this.productTypes.DRINKWARE) {
        url = DRINKWARE_MOCKUP_URL;
      } else if (this.input.productType === this.productTypes.STICKER) {
        url = STICKER_MOCKUP_URL;
      }

      return `${url}${query}`;
    },

    blankMockupUrl(item) {
      const url = CLOTHING_MOCKUP_URL;

      const params = {
        blankId: item.blank.id,
        colorId: item.color.id,
      };

      const query = qs.stringify(params, true);

      return `${url}${query}`;
    },

    textureUrl(query) {
      let url = null;

      if (this.input.productType === this.productTypes.DRINKWARE) {
        url = DRINKWARE_TEXTURE_URL;
      }

      return `${url}${query}`;
    },

    imageLoaded(query) {
      const status = this.tryGetRenderStatus(query);
      return status.loaded || status.error;
    },

    imageLoading(query) {
      const status = this.tryGetRenderStatus(query);
      return !status.loaded && !status.error;
    },

    onImageLoaded(query) {
      const status = this.tryGetRenderStatus(query);
      status.loaded = true;
    },

    onImageError(query) {
      const status = this.tryGetRenderStatus(query);
      status.error = true;
    },

    artworkQuery(options) {
      const params = {};

      if (!options.pathInfo) {
        params.path = "";
      } else {
        params.path = `${options.pathInfo.dirname}/${options.pathInfo.basename}`;
      }

      const allowedOptions = ["w", "local"];

      allowedOptions.forEach((allowedOption) => {
        if (allowedOption in options) {
          params[allowedOption] = options[allowedOption];
        }
      });

      for (const key in params) {
        if (typeof params[key] === "boolean") {
          params[key] = +params[key];
        }
      }

      const query = qs.stringify(params, true);
      return query;
    },

    mockupQuery(options, item) {
      if (this.input.productType === this.productTypes.APPAREL) {
        options.productionMethod = this.input.productionMethod;
        options.allowArtworkScalingToFitBlank = this.input.productionMethod === this.productionMethods.DIRECTTOGARMENT;
      }

      if (item) {
        if (this.input.productType === this.productTypes.APPAREL) {
          options.blankId = item.blank.id;
          options.colorId = item.color.id;
        } else if (this.input.productType === this.productTypes.PHONECASE) {
          options.phoneCaseId = item.code;
          options.backgroundColor = item.hex;
        } else if (this.input.productType === this.productTypes.HAT) {
          options.hatCode = item.code;
          options.color = item.color;
        } else if (this.input.productType === this.productTypes.BEANIE) {
          options.beanieCode = item.code;
          options.color = item.color;
        } else if (this.input.productType === this.productTypes.TOTE) {
          options.code = item.code;
          options.color = item.color;
        } else if (this.input.productType === this.productTypes.DRINKWARE) {
          options.code = item.code;
        } else if (this.input.productType === this.productTypes.STICKER) {
          options.code = item.code;
          options.size = item.size;
        }
      }

      const params = {
        // Null pathInfo (e.g. the back preview before its artwork uploads)
        // sends an empty path — the server renders the blank garment.
        path: options.pathInfo ? `${options.pathInfo.dirname}/${options.pathInfo.basename}` : "",
        productType: this.input.productType,
        ...this.input.mockupOptions,
        ...options,
      };

      delete params.pathInfo;

      for (const key in params) {
        if (typeof params[key] === "boolean") {
          params[key] = +params[key];
        }
      }

      const query = qs.stringify(params, true);
      return query;
    },

    // ARTWORK
    async toggleNoTrim() {
      this.noTrim = !this.noTrim;

      if (this.originalUploadMeta) {
        const source = this.noTrim ? this.originalUploadMeta.untrimmed : this.originalUploadMeta;

        this.input.artwork.rasterArtworkPathInfo = source.pathInfo;
        this.rasterGeometry = source.geometry;
        this.topLeftHex = source.topLeftHex;
      }
    },

    // Raster Artwork Upload
    dzRasterArtworkAdded(file) {
      if (this.rasterFile) {
        this.$refs.rasterArtworkDropzone.removeFile(this.rasterFile);
        this.input.artwork.rasterArtworkPathInfo = null;
        this.rasterGeometry = null;
        this.topLeftHex = null;
        this.originalUploadMeta = null;
      }

      this.rasterFile = file;
      this.isRasterFileUploading = true;
    },

    // Use a staged raster (the upload endpoint's answer, or an artwork edit by the account
    // assistant's edit_artwork) as the artwork: trimmed, or untrimmed with "Preserve artwork
    // padding" on.
    useStagedArtwork(response) {
      const source = this.noTrim ? response.untrimmed : response;

      this.input.artwork.rasterArtworkPathInfo = source.pathInfo;
      this.rasterGeometry = source.geometry;
      this.topLeftHex = source.topLeftHex;

      this.originalUploadMeta = response;
    },

    // The second print location's equivalent of useStagedArtwork.
    useStagedSecondArtwork(response) {
      const source = this.secondNoTrim ? response.untrimmed : response;

      this.input.secondLocation.artwork.rasterArtworkPathInfo = source.pathInfo;
      this.secondRasterGeometry = source.geometry;
      this.secondOriginalUploadMeta = response;
    },

    async dzRasterArtworkSuccess(file, response) {
      this.isRasterFileUploading = false;

      this.useStagedArtwork(response);

      this.onChangeStep(this.steps.SOURCE);

      await this.$nextTick();
      this.$scrollTo(".nylon-SubmissionWizard-options");
    },

    dzRasterArtworkError(file, message, xhr) {
      this.isRasterFileUploading = false;
    },

    dzRasterArtworkMaxFilesExceeded(file, a, b) {
      this.$refs.rasterArtworkDropzone.removeFile(file);
    },

    // Raster Artwork Upload 2
    dzRasterArtworkAdded2(file) {
      if (this.rasterFile) {
        this.$refs.rasterArtworkDropzone2.removeFile(this.rasterFile);
        this.input.artwork.rasterArtworkPathInfo = null;
        this.rasterGeometry = null;
        this.topLeftHex = null;
        this.originalUploadMeta = null;
      }

      this.rasterFile = file;
      this.isRasterFileUploading = true;
    },

    dzRasterArtworkSuccess2(file, response) {
      this.isRasterFileUploading = false;

      this.useStagedArtwork(response);

      this.$refs.rasterArtworkDropzone2.removeFile(file);
    },

    dzRasterArtworkError2(file, message, xhr) {
      this.isRasterFileUploading = false;
    },

    dzRasterArtworkMaxFilesExceeded2(file, a, b) {
      this.$refs.rasterArtworkDropzone2.removeFile(file);
    },

    // Source Artwork Upload
    dzSourceArtworkAdded(file) {
      if (this.sourceFile) {
        this.$refs.sourceArtworkDropzone.removeFile(this.sourceFile);
        this.input.artwork.sourceArtworkPathInfo = null;
      }

      this.sourceFile = file;
      this.isSourceFileUploading = true;
    },

    dzSourceArtworkSuccess(file, response) {
      this.isSourceFileUploading = false;
      this.input.artwork.sourceArtworkPathInfo = response.pathInfo;
    },

    dzSourceArtworkError(file, message, xhr) {
      this.isSourceFileUploading = false;
    },

    dzSourceArtworkMaxFilesExceeded(file, a, b) {
      this.$refs.sourceArtworkDropzone.removeFile(file);
    },

    canPrintOnBlank(blank) {
      if (this.input.productType !== this.productTypes.APPAREL) {
        return true;
      }

      if (!this.locationFitsBlank(blank, this.input.placement, this.input.printGeometry, this.rasterGeometry, this.input.productionMethod)) {
        return false;
      }

      // A back print must fit too (PRO-3173) — screen print can't shrink it.
      const second = this.input.secondLocation;

      if (second?.artwork.rasterArtworkPathInfo) {
        return this.locationFitsBlank(blank, second.placement, second.printGeometry, this.secondRasterGeometry, second.productionMethod);
      }

      return true;
    },

    clampOffsets(range, location, debouncedLocation) {
      ["x", "y"].forEach((axis) => {
        const value = Math.min(range.max[axis], Math.max(range.min[axis], Number(location.printOffset[axis]) || 0));

        if (value !== location.printOffset[axis]) {
          location.printOffset[axis] = debouncedLocation.printOffset[axis] = value;
        }
      });
    },

    // Whether one print location can go on a blank: the location must be
    // printable on it, and for the methods that print artwork at its true size
    // the fitted artwork must fit the blank's printable area. Mirrors
    // App\Support\PrintLocation::fitsBlank.
    locationFitsBlank(blank, placement, printGeometry, artworkGeometryInPixels, productionMethod) {
      const location = placementToBlankLocationMap[placement];

      if (blank.invalid_locations?.includes(location)) {
        return false;
      }

      // Direct-to-garment is never refused for size on purpose: Monster
      // scales an oversized art file down to the garment's real printable
      // area itself, so the nominal box always "fits". The size check below
      // is only for the methods where nobody downstream will shrink the art.
      // See Submission::scalePrintableAreaToFitBlank for the consequences.
      if (![this.productionMethods.SCREENPRINT, this.productionMethods.EMBROIDERY].includes(productionMethod) || !artworkGeometryInPixels) {
        return true;
      }

      const printableAreaInInches = customPlacements.includes(placement)
        ? { width: printGeometry.width, height: printGeometry.height }
        : { width: placementToBoxMap[placement].width, height: placementToBoxMap[placement].height };

      const calculatedGeometry = calculateArtworkGeometry(1, printableAreaInInches, artworkGeometryInPixels);

      // Embroidered pockets are bounded by the pocket's custom-size maximum,
      // not the blank's pocket columns.
      const max =
        productionMethod === this.productionMethods.EMBROIDERY && location === "pocket"
          ? this.customSize.max
          : { width: Number(blank[`${location}_max_width`]), height: Number(blank[`${location}_max_height`]) };

      return max.width >= Number(calculatedGeometry.artworkWidthInches) && max.height >= Number(calculatedGeometry.artworkHeightInches);
    },

    // BUILDER
    onSelectColorPreview(color) {
      this.input.colors.forEach((color) => (color.isActive = false));

      if (this.input.colors.some((c) => toRaw(c) === toRaw(color))) {
        color.isActive = true;
      } else {
        const matchingColor = this.input.colors.find((color) => true);

        if (!!matchingColor) {
          matchingColor.isActive = true;
        }
      }

      const activeItem = color.bundle.items.find((item) => item.isActive);

      if (!activeItem) {
        let firstItem = null;

        if (this.input.productType === this.productTypes.PHONECASE) {
          firstItem = this.itemsGroupedByCase(color.bundle.items)
            .find((phoneCase) => true)
            .items.find((item) => item.isIncludedAndValid);
        } else if (Object.values(this.productTypes).includes(this.input.productType)) {
          firstItem = color.bundle.items.find((item) => item.isIncludedAndValid);
        }

        if (!!firstItem) {
          this.onSelectGroup(color, this.getItemGroupKey(firstItem));
          this.onSelectItemPreview(firstItem);
        }
      }
    },

    onSelectGroup(color, groupKey) {
      if (this.input.productType === this.productTypes.PHONECASE) {
        if (toRaw(this.activeColor) !== toRaw(color)) {
          this.onSelectColorPreview(color);
        }

        this.activeColor.activeGroupKey = groupKey;

        if (!this.activeItem || this.getItemGroupKey(this.activeItem) !== this.activeColor.activeGroupKey) {
          const firstItem = this.itemsGroupedByCase(this.activeColor.bundle.items)
            .find((phoneCase) => phoneCase.name === this.activeColor.activeGroupKey)
            .items.find((item) => true);

          if (!!firstItem) {
            this.onSelectItemPreview(firstItem);
          }
        }
      }
    },

    onSelectItemPreview(item) {
      this.activeColor.bundle.items.forEach((item) => (item.isActive = false));
      item.isActive = true;
    },

    canAddBundle(bundle) {
      if (this.input.productType === this.productTypes.APPAREL) {
        if (bundle.items.every((item) => !item.canBeIncluded)) {
          return false;
        }
      }

      if (this.input.productType === this.productTypes.STICKER) {
        if (bundle.name === "Custom") {
          return true;
        } else {
          return this.input.colors.every((color) => color.size !== bundle.size);
        }
      }

      return this.input.colors.every((color) => color.name !== bundle.name);
    },

    onPlacementChange(placement) {
      this.input.placement = placement;

      if (this.input.productType === this.productTypes.PHONECASE) {
        if (this.topLeftHex !== null && this.input.placement === "max+centered" && this.input.colors.length === 0) {
          if (this.topLeftHex.length === 7) {
            // RGB hex
            this.onChooseColor(undefined, undefined, this.topLeftHex);
          } else if (this.topLeftHex.length === 9) {
            // RGBA hex
            // What is to be done about alpha here? Discard?
            const alphaValue = parseInt(this.topLeftHex.substring(7, 9), 16);
            if (alphaValue === 255) {
              this.onChooseColor(undefined, undefined, this.topLeftHex.substring(0, 7));
            }
          }
        }
      }
    },

    onClickNumInks(numInks) {
      this.input.numInks = numInks;
    },

    onInkTypeChange(name) {
      this.input.inkType = name;
    },

    onInkEffectChange(name) {
      this.input.inkEffect = name;
    },

    // Move the primary location between the front and the back (PRO-3173: a
    // back-only product). A back primary can't carry a back print as well.
    onPrimarySideChange(side) {
      if (side === this.primarySide) {
        return;
      }

      if (side === "back") {
        this.onRemoveSecondLocation();
      }

      this.onPlacementChange(side === "back" ? "back+large+centered" : "large+centered");

      const size = this.customSize.default;
      this.input.printGeometry.width = this.debouncedInput.printGeometry.width = size.width;
      this.input.printGeometry.height = this.debouncedInput.printGeometry.height = size.height;
      this.input.printOffset.x = this.debouncedInput.printOffset.x = 0;
      this.input.printOffset.y = this.debouncedInput.printOffset.y = 0;
    },

    // The included/valid flags and default pricing every item of a newly chosen
    // non-apparel color starts with. canBeIncluded leaves an item out of the color
    // (drinkware blanks that can't take one); hex is set when given.
    prepareChosenColorItems(items, { canBeIncluded = () => true, hex } = {}) {
      items.forEach((item) => {
        const included = canBeIncluded(item);

        item.isIncluded = included;
        item.isActive = false;
        item.canBeIncluded = included;
        item.canBePrinted = true;
        item.isStocked = true;
        item.isValid = included;

        item.isIncludedAndValid = computed(() => {
          return item.isIncluded && item.isValid;
        });

        this.initItemPricing(item, this.baseProfit);

        if (hex !== undefined) {
          item.hex = hex;
        }
      });

      return items;
    },

    onChooseColor(event, bundleIndex, chosenHex) {
      if (this.input.productType === this.productTypes.APPAREL) {
        const bundle = this.bundles[bundleIndex];
        const position = this.input.colors.length + 1;

        this.isChoosingColor = false;

        // Reset the default included status
        bundle.items.forEach((item) => {
          item.isIncluded = item.default && item.available && item.canBePrinted && item.isStocked;
        });

        const color = {
          name: bundle.name,
          position: position,
          isEditing: false,
          isActive: true,
          bundle: bundle,
        };

        this.input.colors.push(color);
        this.onSelectColorPreview(color);
      } else if (this.input.productType === this.productTypes.PHONECASE) {
        let bundle = null;
        let hex = null;

        if (bundleIndex !== undefined) {
          bundle = this.bundles[bundleIndex];
        } else if (event) {
          hex = event.target.value;
        } else if (chosenHex !== undefined) {
          hex = chosenHex;
        }

        const position = this.input.colors.length + 1;

        this.isChoosingColor = false;

        const items = this.prepareChosenColorItems(
          reactive(clone(this.phoneCases)).filter((item) => item.enabled),
          { hex: bundle ? bundle.hex : hex },
        );

        const color = {
          name: bundle ? bundle.name : hex,
          position: position,
          isEditing: false,
          isActive: true,
          activeGroupKey: null,
          bundle: {
            name: bundle ? bundle.name : hex,
            hex: bundle ? bundle.hex : hex,
            items: items,
          },
        };

        this.input.colors.push(color);
        this.onSelectColorPreview(color);
      } else if ([this.productTypes.HAT, this.productTypes.BEANIE, this.productTypes.TOTE].includes(this.input.productType)) {
        const bundles = {
          [this.productTypes.HAT]: this.hatBundles,
          [this.productTypes.BEANIE]: this.beanieBundles,
          [this.productTypes.TOTE]: this.toteBundles,
        }[this.input.productType];
        const bundle = bundles[bundleIndex];
        const position = this.input.colors.length + 1;

        this.isChoosingColor = false;

        const items = this.prepareChosenColorItems(reactive(clone(bundle.items)), { hex: bundle.hex });

        const color = {
          name: bundle.name,
          position: position,
          isEditing: false,
          isActive: true,
          bundle: {
            name: bundle.name,
            hex: bundle.hex,
            items: items,
          },
        };

        this.input.colors.push(color);
        this.onSelectColorPreview(color);
      } else if (this.input.productType === this.productTypes.DRINKWARE) {
        let bundle = null;
        let hex = null;

        if (bundleIndex !== undefined) {
          bundle = this.bundles[bundleIndex];
        } else if (event) {
          hex = event.target.value;
        } else if (chosenHex !== undefined) {
          hex = chosenHex;
        }

        const position = this.input.colors.length + 1;

        this.isChoosingColor = false;

        let items = reactive(
          clone(
            this.drinkware.flatMap((drink) => {
              return drink.blanks.map((blank) => ({ name: drink.name, code: blank.code, surfaceDescription: blank.surfaceDescription, model: blank.model, camera: blank.camera }));
            }),
          ),
        );

        // The steel and black blanks only come uncoloured.
        const isColored = Boolean(bundle || (hex && hex !== "#00000000"));

        this.prepareChosenColorItems(items, {
          hex: bundle ? bundle.hex : hex,
          canBeIncluded: (item) => !(isColored && ["tA20sSteel", "tR20sBlack"].includes(item.code)),
        });

        const color = {
          name: bundle ? bundle.name : hex,
          position: position,
          isEditing: false,
          isActive: true,
          bundle: {
            name: bundle ? bundle.name : hex,
            hex: bundle ? bundle.hex : hex,
            items: items,
          },
        };

        this.input.colors.push(color);
        this.onSelectColorPreview(color);
      } else if (this.input.productType === this.productTypes.STICKER) {
        let bundle = null;
        let size = null;
        let sizeLabel = null;

        if (bundleIndex !== undefined) {
          bundle = this.stickers.bySize[bundleIndex];
        } else if (event) {
          size = event.target.value;
        } else if (chosenHex !== undefined) {
          size = chosenHex;
        }

        const position = this.input.colors.length + 1;

        this.isChoosingColor = false;

        let items = null;

        const isCustom = !bundle && size === "Custom";

        if (bundle) {
          items = reactive(clone(bundle.blanks.filter((blank) => blank.enabled).map((blank) => ({ name: bundle.name, code: blank.code, finish: blank.finish, color: blank.color }))));
        } else if (isCustom) {
          sizeLabel = `Custom ${this.input.colors.filter((color) => color.name.includes("Custom")).length + 1}`;
          items = reactive(clone(this.stickers.custom.blanks.filter((blank) => blank.enabled).map((blank) => ({ name: blank.name, code: blank.code, finish: blank.finish, color: blank.color }))));
        }

        this.prepareChosenColorItems(items);

        items.forEach((item) => {
          item.size = bundle?.size;
          item.actualSizeFormatted = bundle?.size;
          item.isCustom = isCustom;
        });

        const color = reactive({
          name: null,
          size: bundle?.size,
          position: position,
          isEditing: false,
          isActive: true,
          isCustom: isCustom,
          bundle: {
            name: null,
            size: bundle?.size,
            actualSizeFormatted: bundle?.size,
            items: items,
          },
          debouncedInput: {
            printGeometry: {
              width: this.customSize.default.width,
              height: this.customSize.default.height,
              unit: "in",
            },
          },
          printGeometry: {
            width: this.customSize.default.width,
            height: this.customSize.default.height,
            unit: "in",
          },
        });

        const computedSize = computed(() => {
          if (isCustom) {
            return `${color.printGeometry.width}x${color.printGeometry.height}`;
          } else {
            return bundle.size;
          }
        });

        const computedActualSizeFormatted = computed(() => {
          if (isCustom) {
            return calculateAndFormatArtworkGeometry(color.printGeometry, this.rasterGeometry);
          } else {
            return parseAndFormatArtworkGeometry(bundle.size, this.rasterGeometry);
          }
        });

        const computedSizeLabel = computed(() => {
          if (isCustom) {
            return `${sizeLabel}: ${computedActualSizeFormatted.value}`;
          } else {
            return computedActualSizeFormatted.value;
          }
        });

        color.name = computedSizeLabel;
        color.size = computedSize;
        color.bundle.name = computedSizeLabel;
        color.bundle.size = computedSize;
        color.bundle.actualSizeFormatted = computedActualSizeFormatted;

        color.bundle.items.forEach((item) => {
          item.size = computedSize;
          item.actualSizeFormatted = computedActualSizeFormatted;
        });

        this.input.colors.push(color);
        this.onSelectColorPreview(color);

        if (color.isCustom) {
          this.onEditColor(color);
        }
      }

      // The new color's items join their price groups at the price already shown.
      this.syncPriceGroups();
    },

    onCancelChooseColor() {
      this.isChoosingColor = false;
    },

    onAddColor(color) {
      color.isEditing = false;
    },

    onAddAnotherColor() {
      this.isChoosingColor = true;
    },

    onEditColor(color) {
      color.isEditing = true;
      this.onSelectColorPreview(color);
    },

    onDeleteColor(index) {
      const deletedColor = this.input.colors[index];

      this.input.colors.splice(index, 1);

      if (deletedColor.isActive && this.input.colors.length > 0) {
        this.onSelectColorPreview(this.input.colors[0]);
      }
    },

    onCancelColor(color) {
      color.isEditing = false;
    },

    onMakePrimaryColor(index) {
      this.input.colors = arrayMoveImmutable(this.input.colors, index, 0);

      // The default variant must live in the primary color; re-home it.
      this.syncDefaultItem();
    },

    onChooseDefaultItem(item) {
      // Radio semantics: exactly one item is the default. The star only renders
      // on the primary color (colors[0]), so this always lands there.
      this.input.colors.forEach((color) => {
        color.bundle.items.forEach((candidate) => {
          candidate.isDefault = candidate === item;
        });
      });

      // The default must be part of the product — starring an excluded item
      // restores it to the bundle.
      item.isIncluded = true;
    },

    // Guarantees exactly one default: an included+valid blank in the primary
    // color (colors[0]). The backend narrows the flagged blank+color to a
    // specific size. Called on color add/remove (watcher), make-primary, and
    // before submit so the posted `colors` always carry a valid default.
    syncDefaultItem() {
      const colors = this.input.colors;

      // Drop stray flags outside the primary color or on no-longer-valid items.
      colors.forEach((color, colorIndex) => {
        color.bundle.items.forEach((item) => {
          if (item.isDefault && (colorIndex !== 0 || !item.isIncludedAndValid)) {
            item.isDefault = false;
          }
        });
      });

      const primary = colors[0];

      if (!primary) {
        return;
      }

      const candidates = primary.bundle.items.filter((item) => item.isIncludedAndValid);

      if (candidates.length === 0) {
        return;
      }

      // Collapse to a single default; seed the first candidate if none survived.
      let hasDefault = false;

      candidates.forEach((item) => {
        if (item.isDefault) {
          if (hasDefault) {
            item.isDefault = false;
          } else {
            hasDefault = true;
          }
        }
      });

      if (!hasDefault) {
        candidates[0].isDefault = true;
      }
    },

    onPrintWidthInput: debounce(function (event, debouncedGeometry, realGeometry) {
      const target = event.target;
      let value = Number(debouncedGeometry.width);

      if (Number.isNaN(value)) {
        return;
      }

      if (value < target.min) {
        value = debouncedGeometry.width = target.min;
      }

      if (value > target.max) {
        value = debouncedGeometry.width = target.max;
      }

      realGeometry.width = value;
    }, 500),

    onPrintHeightInput: debounce(function (event, debouncedGeometry, realGeometry) {
      const target = event.target;
      let value = Number(debouncedGeometry.height);

      if (Number.isNaN(value)) {
        return;
      }

      if (value < target.min) {
        value = debouncedGeometry.height = target.min;
      }

      if (value > target.max) {
        value = debouncedGeometry.height = target.max;
      }

      realGeometry.height = value;
    }, 500),

    // Top-level options-container fold state (the wizard's boxes and the edit
    // page's Front/Back/Colors/Product Details share collapsedBoxes from
    // createDefaultData()).
    onToggleBox(key) {
      this.collapsedBoxes[key] = !this.collapsedBoxes[key];
    },

    // Scroll a top-level box into view once the collapse/expand animation
    // settles. vue-scrollto computes the target position up front, so
    // scrolling while a box above is still animating lands the viewport at
    // the pre-animation coordinates — wait out the NylonCollapse transition
    // (0.3s) first, and clear the fixed site header.
    async scrollToBoxAfterCollapse(target) {
      await new Promise((resolve) => setTimeout(resolve, 350));
      const headerHeight = document.querySelector(".nylon-Header")?.offsetHeight ?? 0;
      this.$scrollTo(target, { offset: -(headerHeight + 12) });
    },

    // SECOND PRINT LOCATION
    // A back print, printed the primary's way (canAddSecondLocation gates on
    // Product::SECOND_LOCATION_METHODS): on-demand DTG, or screen print on a
    // campaign (PRO-3173), which adds its own inks and source file. The
    // standard back placements + custom. Shared by the wizard (server
    // contract: post_submit_designs) and the account edit page
    // (post_ajax_edit_mockup).
    async onAddSecondLocation() {
      this.seedSecondLocation();

      // Focus shifts to the new location: fold the first out of the way.
      this.collapsedBoxes.artwork = true;
      this.collapsedBoxes.secondLocation = false;

      await this.$nextTick();
      await this.scrollToBoxAfterCollapse("#second-location-container");
    },

    // The second location's state, at its defaults (the add button, and a
    // relaunched campaign's back print before its saved values go on top).
    seedSecondLocation() {
      const placement = "back+large+centered";
      const size = getCustomSize(this.input.productType, placement);

      this.input.secondLocation = {
        artwork: {
          rasterArtworkPathInfo: null,
          sourceArtworkPathInfo: null,
        },
        productionMethod: this.input.productionMethod,
        numInks: 1,
        inkType: "plastisol",
        inkEffect: "none",
        placement,
        printGeometry: {
          width: size.default.width,
          height: size.default.height,
          unit: "in",
        },
        printOffset: {
          x: 0,
          y: 0,
          unit: "in",
        },
      };

      this.debouncedSecondInput = {
        printGeometry: {
          width: size.default.width,
          height: size.default.height,
          unit: "in",
        },
        printOffset: {
          x: 0,
          y: 0,
          unit: "in",
        },
      };
    },

    onRemoveSecondLocation() {
      if (!this.input.secondLocation) {
        return;
      }

      // Scroll back to the re-expanded Artwork box only when it was folded by
      // the add flow — programmatic removals (product-type / sales-method /
      // production-method changes) shouldn't yank the viewport.
      const artworkWasFolded = this.collapsedBoxes.artwork;

      this.collapsedBoxes.artwork = false;
      this.input.secondLocation = null;
      this.secondRasterFile = null;
      this.isSecondRasterFileUploading = false;
      this.secondSourceFile = null;
      this.isSecondSourceFileUploading = false;
      this.secondRasterGeometry = {
        width: 1,
        height: 1,
      };
      this.secondNoTrim = false;
      this.secondOriginalUploadMeta = null;

      if (artworkWasFolded) {
        this.scrollToBoxAfterCollapse("#artwork-container");
      }
    },

    toggleSecondNoTrim() {
      this.secondNoTrim = !this.secondNoTrim;

      if (this.secondOriginalUploadMeta) {
        const source = this.secondNoTrim ? this.secondOriginalUploadMeta.untrimmed : this.secondOriginalUploadMeta;

        this.input.secondLocation.artwork.rasterArtworkPathInfo = source.pathInfo;
        this.secondRasterGeometry = source.geometry;
      }
    },

    // Second-location source file (screen print), mirroring the primary's.
    dzSecondSourceArtworkAdded(file) {
      if (this.secondSourceFile) {
        this.$refs.secondSourceArtworkDropzone.removeFile(this.secondSourceFile);
        this.input.secondLocation.artwork.sourceArtworkPathInfo = null;
      }

      this.secondSourceFile = file;
      this.isSecondSourceFileUploading = true;
    },

    dzSecondSourceArtworkSuccess(file, response) {
      this.isSecondSourceFileUploading = false;
      this.input.secondLocation.artwork.sourceArtworkPathInfo = response.pathInfo;
    },

    dzSecondSourceArtworkError(file, message, xhr) {
      this.isSecondSourceFileUploading = false;
    },

    dzSecondSourceArtworkMaxFilesExceeded(file) {
      this.$refs.secondSourceArtworkDropzone.removeFile(file);
    },

    // Second-location raster artwork upload
    dzSecondRasterArtworkAdded(file) {
      if (this.secondRasterFile) {
        this.$refs.secondRasterArtworkDropzone.removeFile(this.secondRasterFile);
        this.input.secondLocation.artwork.rasterArtworkPathInfo = null;
        this.secondRasterGeometry = {
          width: 1,
          height: 1,
        };
        this.secondOriginalUploadMeta = null;
      }

      this.secondRasterFile = file;
      this.isSecondRasterFileUploading = true;
    },

    dzSecondRasterArtworkSuccess(file, response) {
      this.isSecondRasterFileUploading = false;

      this.useStagedSecondArtwork(response);

      this.$refs.secondRasterArtworkDropzone.removeFile(file);
    },

    dzSecondRasterArtworkError(file, message, xhr) {
      this.isSecondRasterFileUploading = false;
    },

    dzSecondRasterArtworkMaxFilesExceeded(file) {
      this.$refs.secondRasterArtworkDropzone.removeFile(file);
    },

    // Second print location (back print) equivalents of the handlers above.
    // Deliberately separate debounced functions: debounce() keeps only the
    // latest call's arguments, so sharing one function between the front and
    // back panels would drop whichever edit came first in the window.
    onSecondPlacementChange(placement) {
      this.input.secondLocation.placement = placement;

      // Entering the back-neck placement conforms the location to the BN
      // contract the server enforces (Submission::assertValidCollarPrint):
      // dims within 10x12, no vertical offset — Monster's BN origin is fixed
      // 2in below the collar, and fulfillment still applies whatever offset
      // is stored to the canvas.
      if (placement === backNeckPlacement) {
        const max = getCustomSize(this.input.productType, placement).max;
        const geometry = this.input.secondLocation.printGeometry;

        geometry.width = Math.min(geometry.width, max.width);
        geometry.height = Math.min(geometry.height, max.height);
        this.debouncedSecondInput.printGeometry.width = geometry.width;
        this.debouncedSecondInput.printGeometry.height = geometry.height;

        this.input.secondLocation.printOffset.y = 0;
        this.debouncedSecondInput.printOffset.y = 0;
      }
    },

    onSecondPrintWidthInput: debounce(function (event) {
      const target = event.target;
      let value = Number(this.debouncedSecondInput.printGeometry.width);

      if (Number.isNaN(value)) {
        return;
      }

      if (value < target.min) {
        value = this.debouncedSecondInput.printGeometry.width = target.min;
      }

      if (value > target.max) {
        value = this.debouncedSecondInput.printGeometry.width = target.max;
      }

      this.input.secondLocation.printGeometry.width = value;
    }, 500),

    onSecondPrintHeightInput: debounce(function (event) {
      const target = event.target;
      let value = Number(this.debouncedSecondInput.printGeometry.height);

      if (Number.isNaN(value)) {
        return;
      }

      if (value < target.min) {
        value = this.debouncedSecondInput.printGeometry.height = target.min;
      }

      if (value > target.max) {
        value = this.debouncedSecondInput.printGeometry.height = target.max;
      }

      this.input.secondLocation.printGeometry.height = value;
    }, 500),

    onSecondOffsetXInput: debounce(function (event) {
      const target = event.target;
      let value = Number(this.debouncedSecondInput.printOffset.x);

      if (Number.isNaN(value)) {
        return;
      }

      if (value < target.min) {
        value = this.debouncedSecondInput.printOffset.x = target.min;
      }

      if (value > target.max) {
        value = this.debouncedSecondInput.printOffset.x = target.max;
      }

      this.input.secondLocation.printOffset.x = value;
    }, 500),

    onSecondOffsetYInput: debounce(function (event) {
      const target = event.target;
      let value = Number(this.debouncedSecondInput.printOffset.y);

      if (Number.isNaN(value)) {
        return;
      }

      if (value < target.min) {
        value = this.debouncedSecondInput.printOffset.y = target.min;
      }

      if (value > target.max) {
        value = this.debouncedSecondInput.printOffset.y = target.max;
      }

      this.input.secondLocation.printOffset.y = value;
    }, 500),

    onOffsetXInput: debounce(function (event) {
      const target = event.target;
      let value = Number(this.debouncedInput.printOffset.x);

      if (Number.isNaN(value)) {
        return;
      }

      if (value < target.min) {
        value = this.debouncedInput.printOffset.x = target.min;
      }

      if (value > target.max) {
        value = this.debouncedInput.printOffset.x = target.max;
      }

      this.input.printOffset.x = value;
    }, 500),

    onOffsetYInput: debounce(function (event) {
      const target = event.target;
      let value = Number(this.debouncedInput.printOffset.y);

      if (Number.isNaN(value)) {
        return;
      }

      if (value < target.min) {
        value = this.debouncedInput.printOffset.y = target.min;
      }

      if (value > target.max) {
        value = this.debouncedInput.printOffset.y = target.max;
      }

      this.input.printOffset.y = value;
    }, 500),

    // DETAILS
    onProfitGroupInput: debounce(function (event, priceGroup) {
      const target = event.target;
      const value = this.setGroupPrice(priceGroup, target.value);

      target.value = value ?? priceGroup.displayItem.retail;
    }, 500),

    // A price typed into a price group's input (and the account assistant's set_price): whole
    // dollars within the group's bounds, then either every price moved by the same amount
    // (synchronized prices) or just this group's items. Returns the price set, or null when the
    // input isn't a number.
    setGroupPrice(priceGroup, input) {
      let value;

      try {
        value = Big(input);
      } catch (e) {
        return null;
      }

      if (value.gt(priceGroup.displayItem.max)) {
        value = Big(priceGroup.displayItem.max);
      }

      if (value.lt(priceGroup.displayItem.min)) {
        value = Big(priceGroup.displayItem.min);
      }

      value = value.toFixed(0);

      if (this.synchronizePrices) {
        const oldValue = priceGroup.displayItem.retail;
        const delta = value - oldValue;

        // Adjust all items
        this.input.colors.forEach((color) => {
          color.bundle.items.forEach((item) => {
            const newValue = Big(item.retail).plus(delta);

            if (!newValue.gt(item.max) && !newValue.lt(item.min)) {
              item.retail = newValue.toFixed(0);
            }
          });
        });
      } else {
        for (const item of priceGroup.items) {
          item.retail = value;
        }
      }

      return value;
    },

    basePriceFor(item) {
      // The signed-in seller's own per-user base adjustment (0 for everyone else).
      // Applies to every branch below except screen print and embroidered apparel.
      const tables = this.pricingTables;
      const userAdjustment = this.userBaseAdjustment;

      if (this.input.productType === this.productTypes.APPAREL) {
        if (this.input.productionMethod === this.productionMethods.SCREENPRINT) {
          let basePrice = Big(item.blank.screen_print_base_price);

          if (this.input.numInks > 1) {
            basePrice = basePrice.plus(this.input.numInks - 1);
          }

          basePrice = basePrice.plus(this.inkTypes[this.input.inkType].cost);
          basePrice = basePrice.plus(this.inkEffects[this.input.inkEffect].cost);

          // A back print (PRO-3173) is its own location at a flat base plus its
          // own ink adders — ProductPricing::screenPrintBasePrice.
          const second = this.input.secondLocation;

          if (second) {
            basePrice = basePrice
              .plus(tables.screenPrintSecondLocationBasePrice)
              .plus(Math.max(0, second.numInks - 1))
              .plus(this.inkTypes[second.inkType].cost)
              .plus(this.inkEffects[second.inkEffect].cost);
          }

          return basePrice;
        }

        if (this.input.productionMethod === this.productionMethods.EMBROIDERY) {
          let basePrice = Big(item.blank.embroidery_base_price);

          if (this.input.salesMethod === this.salesMethods.ONDEMAND) {
            basePrice = basePrice.plus(this.embroideryOnDemandAdditionalMargin(item.blank.code));
          }

          return basePrice;
        }

        let basePrice = Big(item.blank.dtg_base_price).plus(userAdjustment);

        // Back print (PRO-3088): the server applies a per-variant base-price
        // surcharge (ProductPricing::BACK_PRINT_BASE_PRICE_SURCHARGE) when a
        // second print location is attached — apply it so the wizard's
        // displayed prices match what lands after submission.
        if (this.input.secondLocation) {
          basePrice = basePrice.plus(tables.dtgBackPrintSurcharge);
        }

        return basePrice;
      }

      if (this.input.productType === this.productTypes.PHONECASE) {
        let style = item.style;

        if (item.magsafe) {
          style = `${style} MagSafe`;
        }

        return Big(tables.casePrices[style] ?? 0).plus(userAdjustment);
      }

      if (this.input.productType === this.productTypes.HAT) {
        return Big(tables.hatPrices[item.code] ?? 0).plus(userAdjustment);
      }

      if (this.input.productType === this.productTypes.BEANIE) {
        return Big(tables.beaniePrices[item.code] ?? 0).plus(userAdjustment);
      }

      if (this.input.productType === this.productTypes.TOTE) {
        return Big(tables.totePrices[item.code] ?? 0).plus(userAdjustment);
      }

      if (this.input.productType === this.productTypes.DRINKWARE) {
        return Big(tables.drinkwarePrices[item.code] ?? 0).plus(userAdjustment);
      }

      if (this.input.productType === this.productTypes.STICKER) {
        return Big(tables.stickerPrice).plus(userAdjustment);
      }

      return null;
    },

    pricingFromCut(item, cut, bonusPriceAdjustment, lifetimeBonus) {
      let profit = Big(0);
      let bonus = Big(0);
      let adjustment = Big(0);

      try {
        profit = Big(cut);
        bonus = Big(lifetimeBonus);
        adjustment = Big(bonusPriceAdjustment);
      } catch (e) {}

      // The adjustment is a signed addend (retail = base + adjustment + cut). Below
      // zero it spends the lifetime bonus to lower the retail price, so it can never
      // spend more than the bonus. Above zero it is extra Cotton Bureau profit — a
      // pro bono seller raising the retail price (their own cut is always $0); it is
      // capped at the same markup a for-profit seller could take as cut, and clamped
      // to zero for everyone else.
      const maxMarkup = this.c_isProBono ? Big(this.pricingTables.maxRetailMarkup) : Big(0);

      if (adjustment.gt(maxMarkup)) {
        adjustment = maxMarkup;
      }

      if (adjustment.lt(bonus.neg())) {
        adjustment = bonus.neg();
      }

      const basePrice = this.basePriceFor(item);
      const retailPrice = basePrice === null ? null : basePrice.plus(adjustment).plus(profit);
      // Only negative adjustments consume the bonus; a positive (markup) adjustment
      // never inflates the payable bonus.
      const netBonus = adjustment.lt(0) ? bonus.plus(adjustment) : bonus;

      return {
        base: basePrice,
        cut: profit,
        bonus: netBonus,
        bonusTotal: bonus,
        bonusPriceAdjustment: adjustment,
        // Pro bono sellers never take home anything — their displayed profit is a
        // flat $0 no matter how the retail price relates to the base price.
        cutPlusBonus: this.c_isProBono ? Big(0) : profit.plus(netBonus),
        retail: retailPrice,
      };
    },

    // Lifetime bonus available to the item, per product type (and per variant code
    // for drinkware, where mugs/pint glasses carry reduced bonus amounts).
    itemLifetimeBonus(item) {
      if (this.input.productType === this.productTypes.DRINKWARE) {
        return this.lifetimeBonusFromLifetimeTier(item.code);
      }

      return this.lifetimeBonusFromLifetimeTier();
    },

    // Puts every item back on the default terms for the current context: the
    // type's default cut (baseProfit: $5, $1 for stickers, $0 pro bono) and no
    // bonus adjustment. Used when pro bono status changes, because a cut chosen
    // under one status means nothing under the other (#390).
    resetItemPricingToDefault() {
      const cut = Big(this.baseProfit).toFixed();

      this.input.colors.forEach((color) => {
        color.bundle.items.forEach((item) => {
          item.chosenCut = cut;
          item.chosenBonusAdjustment = "0";
        });
      });
    },

    // Attaches the reactive pricing chain to an item. The stored sources of truth
    // are `chosenCut` (the seller's margin) and `chosenBonusAdjustment` (a signed
    // addend on the base price: negative spends the lifetime bonus instead of it
    // being paid out, positive is extra Cotton Bureau profit from a pro bono
    // seller raising the retail price); `retail` derives from them, so anything
    // that feeds basePriceFor (numInks, inkType, inkEffect, production method)
    // flows through to the displayed retail price automatically. Writing `retail`
    // (the price inputs) back-solves against the current base. For-profit: the cut
    // absorbs the difference down to $0, and only then is the lifetime bonus
    // consumed, down to base - bonusTotal. Pro bono: the cut is always $0 and the
    // whole difference rides on the adjustment.
    initItemPricing(item, cut, bonusPriceAdjustment = 0) {
      let chosenCut = Big(0);
      let chosenBonusAdjustment = Big(0);

      try {
        chosenCut = Big(cut);
      } catch (e) {}

      try {
        chosenBonusAdjustment = Big(bonusPriceAdjustment);
      } catch (e) {}

      item.chosenCut = chosenCut.toFixed();
      item.chosenBonusAdjustment = chosenBonusAdjustment.toFixed();

      item.pricing = computed(() => {
        return this.pricingFromCut(item, item.chosenCut, item.chosenBonusAdjustment, this.itemLifetimeBonus(item));
      });

      item.retail = computed({
        get: () => {
          return item.pricing.retail.toFixed();
        },
        set: (value) => {
          const basePrice = this.basePriceFor(item);

          try {
            const delta = Big(value).minus(basePrice);

            // pricingFromCut owns the clamps: an adjustment spending beyond the
            // available bonus (or marking up beyond pricingTables.maxRetailMarkup) is capped
            // there, snapping the displayed retail to the floor/ceiling.
            if (this.c_isProBono) {
              // Pro bono sellers never take a cut — the whole delta rides on the
              // adjustment: negative spends the lifetime bonus, positive is extra
              // Cotton Bureau profit.
              item.chosenCut = "0";
              item.chosenBonusAdjustment = delta.toFixed();
            } else if (delta.gte(0)) {
              item.chosenCut = delta.toFixed();
              item.chosenBonusAdjustment = "0";
            } else {
              item.chosenCut = "0";
              item.chosenBonusAdjustment = delta.toFixed();
            }
          } catch (e) {}
        },
      });

      // Shared per-item pricing fields. Defined here (rather than at every
      // item-creation site) so all product types get identical bounds and the
      // submit payloads always carry base/cut/retail/bonusPriceAdjustment.
      item.min = computed(() => {
        return item.pricing.base.minus(item.pricing.bonusTotal).toFixed();
      });

      item.max = computed(() => {
        return item.pricing.base.plus(this.pricingTables.maxRetailMarkup).toFixed();
      });

      item.base = computed(() => {
        return item.pricing.base.toFixed();
      });

      item.cut = computed(() => {
        return item.pricing.cut.toFixed();
      });

      item.bonus = computed(() => {
        return item.pricing.bonus.toFixed();
      });

      item.bonusPriceAdjustment = computed(() => {
        return item.pricing.bonusPriceAdjustment.toFixed();
      });

      item.cutPlusBonus = computed(() => {
        return item.pricing.cutPlusBonus.toFixed();
      });
    },

    // A price group shows one price and its input sets every item in it, so every
    // item sells at the price shown: its first included item's (PRO-2160). Items
    // load with their own stored terms, which leaves a group mixed wherever the
    // seller isn't selling an item yet (it sits at the default cut until included)
    // or legacy variants disagree; a newly added color's items start at the
    // default too. Writing `retail` back-solves each item's terms against its own
    // base, exactly as typing the price into the group's input would. The server
    // refuses a save whose variants of one blank (or non-clothing code) disagree.
    syncPriceGroups() {
      this.itemsGroupedForPricing.forEach((group) => {
        group.priceGroups.forEach((priceGroup) => {
          const shownItem = priceGroup.items.find((item) => item.isIncludedAndValid);

          if (!shownItem) {
            return;
          }

          const retail = shownItem.retail;

          priceGroup.items.forEach((item) => {
            item.retail = retail;
          });
        });
      });
    },

    lifetimeBonusFromLifetimeTier(variantCode = null) {
      // Pro bono sellers keep their lifetime bonus too — since PRO-3051 it is what
      // lets them lower retail prices below the base price.
      if (this.user && this.user.tier) {
        const tier = this.user.tier;
        let productBonus = tier.productTypes[this.input.productType] ?? tier.productTypes.default;

        if (variantCode) {
          productBonus = productBonus.codes[variantCode] ?? productBonus;
        }

        return productBonus.bonusTotal;
      }

      return 0;
    },

    // ProductPricing::embroideryOnDemandAdditionalMargin, from its table.
    embroideryOnDemandAdditionalMargin(code) {
      const margins = this.pricingTables.embroideryOnDemandMargins;

      return Object.hasOwn(margins.codes, code) ? margins.codes[code] : margins.default;
    },

    onSynchronizePricesChange() {
      this.synchronizePrices = !this.synchronizePrices;
    },

    async showModal(name, data) {
      if (data) {
        this.modalData = data;
      } else {
        this.modalData = null;
      }

      await this.$nextTick();

      this.vfm$.open(name);
    },

    initializeBundles() {
      const typeOrder = ["Unisex Tee", "Women’s Tee", "Long Sleeve Tee", "Zip-up Hoodie", "Pullover Crewneck", "Pullover Hoodie", "Pullover Quarter Zip", "Tank", "Youth Tee", "Toddler Tee", "Onesie"];
      const blankOrder = [
        // Unisex Tee
        "1717",
        "6010",
        "3600",
        "6210",
        "5000",
        "3930r",
        "3602",

        // Women’s Tee
        "6710",
        "3900",
        "6610",
        "6400",

        // Long Sleeve Tee
        "3513",
        "3511",
        "3501",
        "3501CVC",

        // Zip-up Hoodie
        "3739",
        "IND4000Z",
        "18600",

        // Pullover Crewneck
        "3901",
        "1566",
        "18000",

        // Pullover Hoodie
        "3719",
        "1567",
        "IND4000",
        "G18500",

        // Pullover Quarter Zip
        "EXP20PQ",

        // Tank
        "3484",
        "3480",
        "3480cvc",

        // Youth Tee
        "5000B",

        // Toddler Tee
        "3321",

        // Onesie
        "4424",
      ];

      this.bundles.forEach((bundle) => {
        bundle.items.forEach((item) => {
          item.isActive = false;

          item.canBeIncluded = computed(() => {
            const matchesProductionMethod = item.production_method === this.input.productionMethod;
            const matchesSalesMethod = item.sales_method === this.input.salesMethod;
            return item.available && matchesProductionMethod && matchesSalesMethod;
          });

          item.canBePrinted = computed(() => {
            return this.canPrintOnBlank(item.blank);
          });

          // Each bundle item is one lane (production + sales method), and the server says whether its
          // colour sells there (BundleService colorSellable: stock and admin overrides).
          item.isStocked = item.colorSellable !== false;

          item.isValid = computed(() => {
            return item.canBeIncluded && item.canBePrinted && item.isStocked;
          });

          item.isIncludedAndValid = computed(() => {
            return item.isIncluded && item.isValid;
          });

          item.group = computed(() => {
            return this.getItemGroupKey(item);
          });

          item.subGroup = computed(() => {
            return item.blank.category;
          });

          this.initItemPricing(item, this.baseProfit);
        });

        // Sort items
        bundle.items.sort((a, b) => {
          if (a.blank.code < b.blank.code) {
            return -1;
          } else if (a.blank.code > b.blank.code) {
            return 1;
          }

          return 0;
        });

        bundle.items.sort(sortFunctionFactory(blankOrder, ["blank", "code"]));
        bundle.items.sort(sortFunctionFactory(typeOrder, "group"));
      });
    },

    sortPhoneCases() {
      const generationOrder = [
        "iPhone 18",
        "iPhone 17",
        "iPhone 16",
        "iPhone 15",
        "iPhone 14",
        "iPhone 13",
        "iPhone 12",
        "iPhone 11",
        "Galaxy S24",
        "Galaxy S23",
        "Galaxy S22",
        "Galaxy S21",
        "Galaxy S20",
      ];
      const phoneOrder = [
        "iPhone 18 Pro",
        "iPhone 18 Pro Max",
        "iPhone 18 Duo",
        "iPhone 17 Pro",
        "iPhone 17 Pro Max",
        "iPhone 17",
        "iPhone 17 Air",
        "iPhone 17e",
        "iPhone 16 Pro",
        "iPhone 16 Pro Max",
        "iPhone 16",
        "iPhone 16 Plus",
        "iPhone 16e",
        "iPhone 15 Pro",
        "iPhone 15 Pro Max",
        "iPhone 15",
        "iPhone 15 Plus",
        "iPhone 14 Pro",
        "iPhone 14 Pro Max",
        "iPhone 14",
        "iPhone 14 Plus",
        "iPhone 13 Pro",
        "iPhone 13 Pro Max",
        "iPhone 13",
        "iPhone 13 Mini",
        "iPhone 12 Pro",
        "iPhone 12 Pro Max",
        "iPhone 12",
        "iPhone 12 Mini",
        "Galaxy S24 Ultra",
        "Galaxy S24 Plus",
        "Galaxy S24",
        "Galaxy S23 Ultra",
        "Galaxy S23 Plus",
        "Galaxy S23",
        "Galaxy S22 Ultra",
        "Galaxy S22 Plus",
        "Galaxy S22",
      ];
      const caseOrder = ["Tough", "Tough MagSafe", "Slim", "Clear", "Clear MagSafe", "Tough Matte", "Tough Matte MagSafe", "Slim Matte"];

      this.phoneCases.sort(sortFunctionFactory(generationOrder, "generation"));
      this.phoneCases.sort(sortFunctionFactory(phoneOrder, "phone"));
      this.phoneCases.sort(sortFunctionFactory(caseOrder, "case_name"));
    },
  },

  watch: {
    // Re-home the default variant whenever colors are added or removed.
    "input.colors.length"() {
      this.syncDefaultItem();
    },

    // Keep the offsets inside their range when it changes: a hat's or beanie's
    // nudge room shrinks as the custom box or artwork grows, and neither takes
    // an X offset; a tee's upward room shrinks as its art gets taller, and an
    // older product may carry a nudge its fulfiller never printed.
    offsetRange(range) {
      this.clampOffsets(range, this.input, this.debouncedInput);
    },

    secondOffsetRange(range) {
      if (range) {
        this.clampOffsets(range, this.input.secondLocation, this.debouncedSecondInput);
      }
    },
  },

  beforeCreate() {
    this.steps = {
      SETUP: "setup",
      ARTWORK: "artwork",
      SOURCE: "source",
      BUILDER: "builder",
      DETAILS: "details",
      COMPLETE: "complete",
    };

    this.productTypes = productTypes;
    this.salesMethods = salesMethods;
    this.productionMethods = productionMethods;
    this.primarySideOptions = primarySideOptions;
  },
};
